/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import {toast} from 'react-toastify'
import {useNavigate, useParams} from 'react-router-dom'
import {resetPassword} from '../core/_requests' // Added resetPassword import
import {useAuth} from '../core/Auth'

export function ResetPassword() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Validation state
  const [errors, setErrors] = useState<{password?: string; confirmPassword?: string}>({})
  const [touched, setTouched] = useState<{password?: boolean; confirmPassword?: boolean}>({})

  const navigate = useNavigate()
  const {token} = useParams()
  const {saveAuth, saveUser, logout} = useAuth()

  const validatePassword = (value: string) => {
    if (!value) return 'Password is required'

    // Check minimum length
    if (value.length < 8) {
      return 'Password must contain minimum 8 characters'
    }

    // Check for at least one uppercase letter
    if (!/[A-Z]/.test(value)) {
      return 'Password must contain at least one capital letter'
    }

    // Check for at least one lowercase letter
    if (!/[a-z]/.test(value)) {
      return 'Password must contain at least one small letter'
    }

    // Check for at least one number
    if (!/[0-9]/.test(value)) {
      return 'Password must contain at least one number'
    }

    // Check for at least one special character
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value)) {
      return 'Password must contain at least one special character'
    }

    return ''
  }

  const validateConfirmPassword = (value: string) => {
    if (!value) return 'Confirm password is required'
    if (value !== password) return 'Passwords do not match'
    return ''
  }

  const validateField = (name: string, value: string) => {
    if (name === 'password') return validatePassword(value)
    if (name === 'confirmPassword') return validateConfirmPassword(value)
    return ''
  }

  const validateAll = () => {
    const e: {password?: string; confirmPassword?: string} = {}
    const passwordErr = validatePassword(password)
    if (passwordErr) e.password = passwordErr
    const confirmPasswordErr = validateConfirmPassword(confirmPassword)
    if (confirmPasswordErr) e.confirmPassword = confirmPasswordErr
    return e
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const {name, value} = e.target
    setTouched((t) => ({...t, [name]: true}))
    const err = validateField(name, value)
    setErrors((prev) => ({...prev, [name]: err || undefined}))
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target

    if (name === 'password') setPassword(value)
    if (name === 'confirmPassword') setConfirmPassword(value)

    // If field was touched or has an error, re-validate on change
    if (
      touched[name as 'password' | 'confirmPassword'] ||
      errors[name as 'password' | 'confirmPassword']
    ) {
      const err = validateField(name, value)
      setErrors((prev) => ({...prev, [name]: err || undefined}))
    }

    // Also re-validate confirm password if password changes
    if (
      name === 'password' &&
      confirmPassword &&
      (touched.confirmPassword || errors.confirmPassword)
    ) {
      const confirmErr = validateConfirmPassword(confirmPassword)
      setErrors((prev) => ({...prev, confirmPassword: confirmErr || undefined}))
    }
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault()
    setLoading(true)

    // Mark all fields touched and validate
    setTouched({password: true, confirmPassword: true})
    const eAll = validateAll()
    setErrors(eAll)

    if (Object.keys(eAll).length > 0) {
      const messages = Object.values(eAll).filter(Boolean).join('. ')
      toast.error(messages || 'Please fix validation errors')
      setLoading(false)
      return
    }

    // Added reset password API call implementation
    try {
      // Call the reset password API with id from URL params and new password
      const resp = await resetPassword(password, confirmPassword, token!)
      if (resp.token && resp.user) {
        saveAuth({api_token: resp.token})
        saveUser(resp.user)
      }
      toast.success(resp.message || 'Password reset successful')
      navigate('/')
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to reset password'
      toast.error(errorMessage)
      logout()
    } finally {
      setLoading(false)
    }
  }

  const isFormValid = !validatePassword(password) && !validateConfirmPassword(confirmPassword)

  return (
    <form className='form w-100' onSubmit={handleSubmit} noValidate>
      <div className='text-center mb-11'>
        <h1 className='text-black fw-bolder mb-4'>Reset Password</h1>
      </div>

      {/* Password */}
      <div className='fv-row mb-8 password-field'>
        <label className='form-label fs-6 fw-bolder text-dark'>
          New Password <span className='text-danger'>*</span>
        </label>

        <div className='input-group'>
          <input
            name='password'
            type={showPassword ? 'text' : 'password'}
            className={`form-control bg-transparent ${touched.password ? (errors.password ? 'is-invalid' : 'is-valid') : ''}`}
            placeholder='Enter new password'
            value={password}
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete='off'
          />
          <button
            type='button'
            className='btn btn-outline-secondary'
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <i className={`bi bi-${showPassword ? 'eye' : 'eye-slash'}`}></i>
          </button>
        </div>
        {errors.password && <div className='invalid-feedback d-block'>{errors.password}</div>}
      </div>

      {/* Confirm Password */}
      <div className='fv-row mb-10 password-field'>
        <label className='form-label fs-6 fw-bolder text-dark'>
          Confirm Password <span className='text-danger'>*</span>
        </label>

        <div className='input-group'>
          <input
            name='confirmPassword'
            type={showConfirmPassword ? 'text' : 'password'}
            className={`form-control bg-transparent ${touched.confirmPassword ? (errors.confirmPassword ? 'is-invalid' : 'is-valid') : ''}`}
            placeholder='Confirm new password'
            value={confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete='off'
          />
          <button
            type='button'
            className='btn btn-outline-secondary'
            onClick={() => setShowConfirmPassword((s) => !s)}
            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
          >
            <i className={`bi bi-${showConfirmPassword ? 'eye' : 'eye-slash'}`}></i>
          </button>
        </div>
        {errors.confirmPassword && (
          <div className='invalid-feedback d-block'>{errors.confirmPassword}</div>
        )}
      </div>

      <div className='d-grid'>
        <button type='submit' className='btn btn-primary' disabled={loading || !isFormValid}>
          {!loading && <span>Reset Password</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </form>
  )
}
